import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { createRef } from 'react';
import { ComponentSync, LL_H_C, ModuleFE_Toaster, TS_PropRenderer } from '@nu-art/thunderstorm-frontend';
import { ModuleFE_Pathway } from '@app/pathway-frontend';
import { asArray, MimeType_html } from '@nu-art/ts-common';
import { ModuleFE_VolatileImport } from '../ModuleFE_VolatileImport.js';
import './Component_PathwayReview.scss';
/**
 * "Review" section rendered into the pathway details editor (`pathway-review` slot). Shows a link that
 * opens/closes the HTML review overlay (Component_PathwayHtml) docked over the graph. In edit mode it
 * also exposes attach/replace/remove controls for the pathway HTML. Renders nothing outside edit mode
 * when the pathway has no HTML. Open/close state and the sibling overlay are coordinated through
 * ModuleFE_VolatileImport (pathway/frontend can't import this package), re-deriving on its dispatcher.
 */
export class Component_PathwayReview extends ComponentSync {
    htmlInputRef = createRef();
    __onPathwayReviewToggled = (reviewKey) => {
        if (reviewKey === `${this.props.tabId}::${this.props.pathwayId}`)
            this.reDeriveState();
    };
    // Re-derive when this pathway's cache entry changes so hasHtml (Attach vs Replace/Remove) and the
    // link stay in sync after an attach/replace/remove, without waiting for a parent re-render.
    __onPathwayUpdated = (...params) => {
        if (asArray(params[1]).every(pathway => pathway._id !== this.props.pathwayId))
            return;
        this.reDeriveState();
    };
    deriveStateFromProps(nextProps, state) {
        const pathway = ModuleFE_Pathway.cache.unique(nextProps.pathwayId);
        const importId = ModuleFE_VolatileImport.getImportIdForPathway(nextProps.tabId, nextProps.pathwayId);
        const pendingFile = importId ? ModuleFE_VolatileImport.getPendingHtmlFile(importId) : undefined;
        state.editMode = !!nextProps.editMode;
        state.hasHtml = !!(pathway?.pathwayHtmlUrl || pendingFile);
        state.isOpen = ModuleFE_VolatileImport.isReviewOpen(nextProps.tabId, nextProps.pathwayId);
        state.pathwayLabel = pathway?.label ?? '';
        state.saving ??= false;
        state.confirmingRemove ??= false;
        // Drop the two-step Remove confirmation when leaving edit mode or switching to another
        // pathway/tab, so it never lingers and re-appears without a fresh click.
        const slotChanged = nextProps.tabId !== this.props.tabId || nextProps.pathwayId !== this.props.pathwayId;
        if (!state.editMode || slotChanged)
            state.confirmingRemove = false;
        return state;
    }
    toggle = () => {
        const { tabId, pathwayId } = this.props;
        this.state.isOpen
            ? ModuleFE_VolatileImport.closeReview(tabId, pathwayId)
            : ModuleFE_VolatileImport.openReview(tabId, pathwayId);
    };
    pickFile = () => this.htmlInputRef.current?.click();
    onFilePicked = async () => {
        const file = this.htmlInputRef.current?.files?.[0];
        if (!file)
            return;
        // Guard against a wrong pick (the `accept` filter is only a hint): reject non-HTML before the
        // upload so the user gets a clear message instead of a generic server error.
        const isHtml = /\.html?$/i.test(file.name) || file.type === MimeType_html;
        if (!isHtml) {
            ModuleFE_Toaster.toastError('Please choose an .html file.');
            if (this.htmlInputRef.current)
                this.htmlInputRef.current.value = '';
            return;
        }
        this.setState({ saving: true });
        try {
            await ModuleFE_Pathway.setPathwayHtml(this.props.pathwayId, file);
            ModuleFE_Toaster.toastSuccess('HTML updated.');
        }
        catch (e) {
            ModuleFE_Toaster.toastError(e instanceof Error ? e.message : 'Failed to upload HTML.');
        }
        finally {
            // Reset so picking the same filename again still fires onChange.
            if (this.htmlInputRef.current)
                this.htmlInputRef.current.value = '';
            if (this.mounted)
                this.setState({ saving: false });
        }
    };
    confirmRemove = async () => {
        this.setState({ saving: true, confirmingRemove: false });
        try {
            await ModuleFE_Pathway.removePathwayHtml(this.props.pathwayId);
            ModuleFE_Toaster.toastSuccess('HTML removed.');
        }
        catch (e) {
            ModuleFE_Toaster.toastError(e instanceof Error ? e.message : 'Failed to remove HTML.');
        }
        finally {
            if (this.mounted)
                this.setState({ saving: false });
        }
    };
    renderActions = () => {
        if (this.state.saving)
            return _jsx("span", { className: 'pathway-review__status', children: "Working\u2026" });
        if (this.state.confirmingRemove)
            return _jsxs(_Fragment, { children: [_jsx("button", { type: 'button', className: 'pathway-review__btn pathway-review__btn--danger', onClick: this.confirmRemove, children: "Confirm remove" }), _jsx("button", { type: 'button', className: 'pathway-review__btn', onClick: () => this.setState({ confirmingRemove: false }), children: "Cancel" })] });
        return _jsxs(_Fragment, { children: [_jsx("button", { type: 'button', className: 'pathway-review__btn', onClick: this.pickFile, children: this.state.hasHtml ? 'Replace' : 'Attach HTML' }), this.state.hasHtml &&
                    _jsx("button", { type: 'button', className: 'pathway-review__btn pathway-review__btn--danger', onClick: () => this.setState({ confirmingRemove: true }), children: "Remove" })] });
    };
    render() {
        const editMode = this.state.editMode;
        if (!editMode && !this.state.hasHtml)
            return null;
        return _jsxs(TS_PropRenderer.Vertical, { label: 'Review:', className: 'pathway-review', children: [_jsxs(LL_H_C, { className: 'pathway-review__row', children: [this.state.hasHtml &&
                            _jsx("div", { className: 'pathway-review__link', onClick: this.toggle, children: this.state.pathwayLabel || 'Open' }), editMode && this.renderActions()] }), _jsx("input", { ref: this.htmlInputRef, type: 'file', accept: '.html,.htm', style: { display: 'none' }, onChange: this.onFilePicked })] });
    }
}
//# sourceMappingURL=Component_PathwayReview.js.map